import React, { useState, useRef, useCallback } from 'react';
import { Box, Text } from '@wix/design-system';
import { UploadState } from '../types';
import { UPLOAD_BRIEF_WIDGET } from '../consts';
import { IdleState, LoadingState, ErrorState } from './UploadState';
import { useBriefUpload } from './useBriefUpload';
import { useDisabledWidget } from '../../../../shared/disabled-widget/useDisabledWidget';
export const UploadBriefWidget = _ref => {
  let {
    data: {
      uploadTitle = 'Upload a brief file',
      supportedFormatsText = 'Supported formats are .pdf, .docx, .pptx',
      errorsMessages = {
        uploadErrorText: 'Couldn’t load this file due to a technical error, please try again.',
        maxSizeErrorText: 'Supported formats are .pdf, .docx, .pptx, up to 5MB',
        tooLongErrorText: 'File is too long'
      },
      analyzeButtonText = 'Analyze Brief',
      uploadButton,
      skipButton
    },
    context: {
      onWidgetAction,
      emitSideEffect,
      httpClient
    },
    config
  } = _ref;
  const [state, setState] = useState(UploadState.IDLE);
  const [file, setFile] = useState();
  const fileInputRef = useRef(null);
  const {
    processFileUpload,
    error
  } = useBriefUpload();
  const {
    areControlsDisabled
  } = useDisabledWidget(config);
  const handleWidgetAction = React.useCallback(actionData => {
    setState(UploadState.COMPLETE);
    onWidgetAction == null || onWidgetAction(UPLOAD_BRIEF_WIDGET, {
      action: actionData.buttonName,
      value: actionData
    });
    emitSideEffect(actionData.sideEffectName, {
      projectInsight: actionData.projectInsight,
      briefFileData: actionData.briefFileData
    });
  }, [onWidgetAction, emitSideEffect]);
  const handleFileChange = async event => {
    var _event$target$files;
    const uploadedFile = (_event$target$files = event.target.files) == null ? void 0 : _event$target$files[0];
    if (!uploadedFile) {
      setState(UploadState.ERROR);
      return;
    }
    setFile(uploadedFile);
    setState(UploadState.LOADING);
    try {
      const response = await processFileUpload(httpClient, uploadedFile, errorsMessages);
      const {
        fileData,
        project
      } = response;
      handleWidgetAction({
        buttonName: uploadButton.buttonName,
        sideEffectName: uploadButton.sideEffectName,
        briefFileData: fileData,
        projectInsight: project
      });
      setState(UploadState.COMPLETE);
    } catch {
      setState(UploadState.ERROR);
      setFile(undefined);
    }
  };
  const handleUpload = useCallback(() => {
    var _fileInputRef$current;
    (_fileInputRef$current = fileInputRef.current) == null || _fileInputRef$current.click();
  }, []);
  const renderState = areControlsDisabled => {
    switch (state) {
      case UploadState.IDLE:
        return /*#__PURE__*/React.createElement(IdleState, {
          onFileUpload: handleUpload,
          onSkipUpload: () => handleWidgetAction({
            buttonName: skipButton.buttonName,
            sideEffectName: skipButton.sideEffectName
          }),
          uploadLabel: uploadButton.label,
          skipLabel: skipButton.label,
          formatDescription: supportedFormatsText,
          isCompleted: areControlsDisabled
        });
      case UploadState.LOADING:
        return /*#__PURE__*/React.createElement(LoadingState, {
          uploadedFileName: (file == null ? void 0 : file.name) || '',
          analyzeLabel: analyzeButtonText,
          skipLabel: skipButton.label
        });
      case UploadState.ERROR:
        return /*#__PURE__*/React.createElement(ErrorState, {
          onRetryUpload: handleUpload,
          onSkipUpload: () => handleWidgetAction({
            buttonName: skipButton.buttonName,
            sideEffectName: skipButton.sideEffectName
          }),
          retryLabel: uploadButton.label,
          skipLabel: skipButton.label,
          errorMessage: error === errorsMessages.maxSizeErrorText ? errorsMessages.maxSizeErrorText : errorsMessages.uploadErrorText
        });
      case UploadState.COMPLETE:
        return /*#__PURE__*/React.createElement(IdleState, {
          onFileUpload: handleUpload,
          onSkipUpload: () => handleWidgetAction({
            buttonName: skipButton.buttonName,
            sideEffectName: skipButton.sideEffectName
          }),
          uploadLabel: uploadButton.label,
          skipLabel: skipButton.label,
          formatDescription: supportedFormatsText,
          isCompleted: areControlsDisabled
        });
      default:
        return null;
    }
  };
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "18px",
    paddingTop: "SP1",
    paddingBottom: "SP1",
    width: "100%"
  }, /*#__PURE__*/React.createElement("input", {
    type: "file",
    style: {
      display: 'none'
    },
    ref: fileInputRef,
    onChange: handleFileChange,
    accept: "application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword"
  }), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    width: "100%",
    gap: "18px"
  }, /*#__PURE__*/React.createElement(Text, {
    weight: "bold",
    skin: areControlsDisabled ? 'disabled' : undefined
  }, uploadTitle), renderState(areControlsDisabled)));
};
//# sourceMappingURL=UploadBriefWidget.js.map